/**
 * apps/web/src/auth/AuthContext.tsx
 *
 * Session/identity context. The API uses an httpOnly cookie session, so the
 * client never holds the token; instead it asks GET /auth/me on boot to learn
 * whether a valid session exists.
 *
 * Exposes:
 *   - status: 'loading' | 'authenticated' | 'unauthenticated'
 *   - user / workspace / membership (null until authenticated)
 *   - login(email, password): authenticate, then refresh identity
 *   - logout(): clear server cookie + local state
 *   - refresh(): re-run getProfile (e.g. after switching workspace)
 *
 * All server reads here are deliberately bare (boot-time identity probe), kept
 * OUT of React Query so the provider can mount above the QueryClient if needed.
 */

import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authApi } from '../api';
import type { AuthSession, Membership, User, Workspace } from '../api/types';
import { isApiError } from '../lib/api';
import { reportError } from '../lib/reportError';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: User | null;
  readonly workspace: Workspace | null;
  readonly membership: Membership | null;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly refresh: () => Promise<void>;
  /**
   * Adopt an already-minted session directly (no round-trip). Used by the
   * magic-link landing route, which receives a full session from the verify
   * endpoint and wants the app to reflect it immediately.
   */
  readonly adoptSession: (session: AuthSession) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<AuthSession | null>(null);

  /**
   * Bumped by every DIRECT session write (login / logout / adoptSession). A
   * getProfile() probe snapshots it before leaving and discards its own answer
   * if the counter moved while it was in flight.
   *
   * This is what makes magic-link sign-in work. The boot probe leaves on mount,
   * BEFORE the landing page has POSTed the token, so it comes back 401 - by
   * which time the cookie is set and adoptSession() has already marked the app
   * authenticated. Without the guard the stale 401 wins by arriving last and
   * bounces a signed-in user to /login (reproduced 3 times in 4 in production).
   */
  const writeEpoch = useRef(0);

  /** Apply a direct session write and invalidate every probe now in flight. */
  const commitSession = useCallback((next: AuthSession | null): void => {
    writeEpoch.current += 1;
    setSession(next);
    setStatus(next ? 'authenticated' : 'unauthenticated');
  }, []);

  const loadProfile = useCallback(async (): Promise<void> => {
    const epoch = writeEpoch.current;
    /** True once a direct write has superseded this probe. */
    const superseded = (): boolean => writeEpoch.current !== epoch;

    try {
      const profile = await authApi.getProfile();
      if (superseded()) return;
      setSession(profile);
      setStatus('authenticated');
    } catch (err) {
      if (superseded()) return;
      // A 401 simply means "no valid session" - the normal unauthenticated
      // state, not an error to surface. A network or server error lands here
      // too and is treated identically (never rethrown), so the app renders the
      // login screen rather than a blank one; isApiError only tells the two
      // apart for the console breadcrumb.
      if (!(isApiError(err) && err.status === 401)) {
        reportError(err, { scope: 'auth-boot-probe' });
      }
      setSession(null);
      setStatus('unauthenticated');
    }
  }, []);

  // Boot-time identity probe.
  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const result = await authApi.login({ email, password });
      // The login response is itself a full session; adopt it directly so the
      // UI updates immediately without a second round-trip.
      commitSession({
        user: result.user,
        workspace: result.workspace,
        membership: result.membership,
      });
    },
    [commitSession],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      commitSession(null);
    }
  }, [commitSession]);

  const adoptSession = useCallback(
    (next: AuthSession): void => {
      commitSession({
        user: next.user,
        workspace: next.workspace,
        membership: next.membership,
      });
    },
    [commitSession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: session?.user ?? null,
      workspace: session?.workspace ?? null,
      membership: session?.membership ?? null,
      login,
      logout,
      refresh: loadProfile,
      adoptSession,
    }),
    [status, session, login, logout, loadProfile, adoptSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
